import { useCallback, useEffect, useState } from 'react';
import { useCollection } from '@cloudscape-design/collection-hooks';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Spinner from '@cloudscape-design/components/spinner';
import Table from '@cloudscape-design/components/table';
import BarChart from '@cloudscape-design/components/bar-chart';
import LineChart from '@cloudscape-design/components/line-chart';
import PieChart from '@cloudscape-design/components/pie-chart';
import { StatsResponse, getStats } from './api';
import BadgePhoto from './BadgePhoto';

interface Props {
  idToken: string;
}

// Show the day and month for a 'YYYY-MM-DD' bucket; fall back to the raw string.
function formatDay(iso: string): string {
  const date = new Date(`${iso}T00:00:00Z`);
  if (isNaN(date.getTime())) {
    return iso;
  }
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function formatDateTime(iso: string): string {
  if (!iso) {
    return '—';
  }
  const date = new Date(iso);
  if (isNaN(date.getTime())) {
    return '—';
  }
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// A single headline metric. A big number is the right form for a KPI — no chart.
function StatTile({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <Box variant="awsui-key-label">{label}</Box>
      <Box fontSize="display-l" fontWeight="bold">
        {value.toLocaleString()}
      </Box>
    </div>
  );
}

export default function StatsPanel({ idToken }: Props) {
  const [stats, setStats] = useState<StatsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setStats(await getStats(idToken));
    } catch (e) {
      setStats(null);
      setError(e instanceof Error ? e.message : 'Failed to load usage statistics.');
    } finally {
      setLoading(false);
    }
  }, [idToken]);

  useEffect(() => {
    void load();
  }, [load]);

  // Client-side sorting for the per-user table. Called unconditionally (before
  // the early returns below) to satisfy the rules of hooks; falls back to an
  // empty list until the stats have loaded. The backend already returns
  // perUser sorted by connections desc, which we mirror as the default state.
  const { items: sortedUsers, collectionProps } = useCollection(stats?.perUser ?? [], {
    sorting: { defaultState: { sortingColumn: { sortingField: 'connections' }, isDescending: true } },
  });

  if (loading) {
    return (
      <Box textAlign="center" padding="xxl">
        <Spinner size="large" />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert type="error" header="Failed to load usage statistics" action={<Button onClick={() => void load()}>Retry</Button>}>
        {error}
      </Alert>
    );
  }

  if (!stats) {
    return null;
  }

  const { totals, connectionsOverTime, perUser, interactionsByKind } = stats;

  // Bar chart is capped to the busiest users so alias labels stay legible; the
  // full breakdown is always available in the table below.
  const TOP_N = 12;
  const topUsers = perUser.slice(0, TOP_N);
  const hiddenUsers = perUser.length - topUsers.length;

  return (
    <SpaceBetween size="l">
      <Container
        header={
          <Header
            variant="h2"
            description={`Aggregated as of ${formatDateTime(stats.generatedAt)}`}
            actions={<Button iconName="refresh" onClick={() => void load()}>Refresh</Button>}
          >
            Adoption overview
          </Header>
        }
      >
        <ColumnLayout columns={4} variant="text-grid">
          <StatTile label="Connections" value={totals.connections} />
          <StatTile label="Unique users" value={totals.uniqueUsers} />
          <StatTile label="Engaged users" value={totals.engagedUsers} />
          <StatTile label="Interactions" value={totals.interactions} />
        </ColumnLayout>
      </Container>

      <Container header={<Header variant="h2" description="Daily app connections and how many distinct people connected.">Connections over time</Header>}>
        <LineChart
          series={[
            {
              title: 'Connections',
              type: 'line',
              data: connectionsOverTime.map((d) => ({ x: d.date, y: d.connections })),
            },
            {
              title: 'Unique users',
              type: 'line',
              data: connectionsOverTime.map((d) => ({ x: d.date, y: d.uniqueUsers })),
            },
          ]}
          xScaleType="categorical"
          xTitle="Date"
          yTitle="Count"
          xTickFormatter={formatDay}
          ariaLabel="Connections over time"
          height={280}
          hideFilter
          empty={<Box textAlign="center" color="inherit">No connections recorded yet.</Box>}
        />
      </Container>

      <ColumnLayout columns={2}>
        <Container header={<Header variant="h2" description="Busiest users by connection count and interactions.">Top users</Header>}>
          <BarChart
            series={[
              {
                title: 'Connections',
                type: 'bar',
                data: topUsers.map((u) => ({ x: u.alias, y: u.connections })),
              },
              {
                title: 'Interactions',
                type: 'bar',
                data: topUsers.map((u) => ({ x: u.alias, y: u.interactions })),
              },
            ]}
            xScaleType="categorical"
            horizontalBars
            xTitle="User"
            yTitle="Count"
            ariaLabel="Top users by activity"
            height={320}
            hideFilter
            empty={<Box textAlign="center" color="inherit">No usage recorded yet.</Box>}
          />
          {hiddenUsers > 0 && (
            <Box color="text-status-inactive" fontSize="body-s" padding={{ top: 's' }}>
              Showing the top {TOP_N} of {perUser.length} users. See the table below for the full list.
            </Box>
          )}
        </Container>

        <Container header={<Header variant="h2" description="How engaged users interact with the tool.">Interaction mix</Header>}>
          <PieChart
            variant="donut"
            data={[
              { title: 'Dialog messages', value: interactionsByKind.dialog },
              { title: 'Scheduling actions', value: interactionsByKind.scheduling },
            ]}
            innerMetricValue={totals.interactions.toLocaleString()}
            innerMetricDescription="interactions"
            ariaLabel="Interaction breakdown by kind"
            size="medium"
            hideFilter
            empty={<Box textAlign="center" color="inherit">No interactions recorded yet.</Box>}
          />
        </Container>
      </ColumnLayout>

      <Table
        {...collectionProps}
        header={<Header variant="h2" counter={`(${perUser.length})`}>Per-user activity</Header>}
        items={sortedUsers}
        empty="No usage recorded yet."
        columnDefinitions={[
          {
            id: 'alias',
            header: 'User',
            cell: (u) => (
              <span className="manager-cell">
                <BadgePhoto alias={u.alias} size={28} />
                {u.alias}
              </span>
            ),
            sortingField: 'alias',
          },
          { id: 'connections', header: 'Connections', cell: (u) => u.connections, sortingField: 'connections' },
          { id: 'interactions', header: 'Interactions', cell: (u) => u.interactions, sortingField: 'interactions' },
          { id: 'dialog', header: 'Dialog msgs', cell: (u) => u.dialogInteractions, sortingField: 'dialogInteractions' },
          { id: 'scheduling', header: 'Scheduling', cell: (u) => u.schedulingInteractions, sortingField: 'schedulingInteractions' },
          { id: 'firstSeen', header: 'First seen', cell: (u) => formatDateTime(u.firstSeen), sortingField: 'firstSeen' },
          { id: 'lastSeen', header: 'Last seen', cell: (u) => formatDateTime(u.lastSeen), sortingField: 'lastSeen' },
        ]}
      />
    </SpaceBetween>
  );
}
